/**
 * Access Level ladder API service — ขั้นบันได T0…T3 (+ ระดับย่อย T2.1 …) (#914)
 *
 * ห่อ endpoint ใต้ `api/v1/AccessLevel` (AccessLevelTierController) แบบ promise ที่ **ไม่เด้ง popup**
 * (ต่างจาก `this.$call.*` ที่เด้ง Swal ทั้งแอปเมื่อ error) ⇒ ใช้โหลดเบื้องหลังใน Flow builder / Stream /
 * DebugControlBar ได้ และหน้า `/AccessLevelConfig` แสดง error เองแบบ inline/snackbar
 *
 * ── path อ่านกับเขียนแยกกันโดยตั้งใจ
 * `APIAuthen` จับคู่สิทธิ์ด้วย path อย่างเดียว (ไม่ดู HTTP method) — `GET /AccessLevel` เปิดถึง role 2/6
 * (dropdown ใน builder/Stream) ⇒ การเขียนจึงอยู่ที่ `/create` แทน `POST /` · code มีจุด (`T2.1`) ⇒ ไม่มี
 * `/usage/{code}` ใช้ `usage()` ทั้งชุดแล้วกรองเอง
 *
 * ── รูปข้อมูล (STJ camelCase)
 * list()/reorder()/remove()  → `{ levels: [{ id, code, nameTh, nameEn, description, sortOrder, isSystem, updateDate, updateBy }], fromDatabase, warning }`
 * usage()                     → `{ intents: [{ flowId, flowGroupId, flowName, companyId, nodeId, nodeType, routerName, intentId,
 *                                 intentName, intentPath[], chainLevels[], rawLevel, effectiveLevel }], countByCode: {code: n},
 *                                 scannedFlows, scannedNodes, warnings[] }`
 * create()/update()           → `{ id, code, nameTh, nameEn, description, sortOrder, isSystem, updateDate, updateBy }`
 *
 * ── error
 * ทุกเมธอด throw `Error` ที่มี `message` (จาก ResponseModel ถ้ามี), `httpStatus` (HTTP code จริง),
 * `status` (APIStatus) และ `data` · `remove()` ที่ติด intent ⇒ `httpStatus === 409` และ
 * `data = { code, intents: [...] }` (AccessLevelDeleteConflictDto)
 *
 * โครง axios / base-URL / cookie-auth / unwrap มาจาก `services/mcpSettingsApi.js` — ต่างกันตรงที่ error ที่
 * `unwrap()` สร้างเองติดธง `isUnwrapped` (แบบ `services/jobLogApi.js`) เพราะ axios 1.x ตั้ง `error.status`
 * ให้ AxiosError ด้วย ⇒ เงื่อนไขเดิม `httpStatus === undefined && status !== undefined` จะปล่อย AxiosError
 * ดิบผ่านไปโดยไม่มี `httpStatus`/`data` (409 ของ `remove()` จะอ่าน conflict ไม่ได้)
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/** แกะ ResponseModel `{ message, status, data }` → `data` · throw เมื่อ envelope บอกว่าไม่สำเร็จ */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.httpStatus = response.status;
    err.data = data;
    err.isUnwrapped = true;
    throw err;
  }
  return body;
}

/** แปลง error ของ axios ให้เป็น Error ที่พก message + HTTP status + data ของ API มาด้วย */
function toError(error) {
  // error ที่ unwrap() สร้างเองไม่มี `response` — ห่อซ้ำแล้ว httpStatus/data จะหาย
  if (error && error.isUnwrapped) return error;

  const body = error && error.response && error.response.data;
  const bodyIsObject = body && typeof body === 'object';
  const err = new Error(
    (bodyIsObject && (body.message || body.Message)) || (error && error.message) || 'Request failed'
  );
  err.httpStatus = error && error.response && error.response.status;
  err.status = bodyIsObject ? body.status ?? body.Status : undefined;
  err.data = bodyIsObject ? body.data ?? body.Data : undefined;
  return err;
}

async function request(send) {
  try {
    return unwrap(await send());
  } catch (e) {
    throw toError(e);
  }
}

const get = (url) => request(() => http.get(url));
const post = (url, payload) => request(() => http.post(url, payload || {}));
const put = (url, payload) => request(() => http.put(url, payload || {}));
const del = (url) => request(() => http.delete(url));

const ROOT = 'api/v1/AccessLevel';

export const accessLevelApi = {
  /**
   * ขั้นบันไดทั้งชุด เรียงตาม rank (SortOrder) · ตารางว่าง/ยังไม่รัน SQL ⇒ ค่าในตัว T0–T3 + `fromDatabase:false`
   * (server ตอบ 200 เสมอ — dropdown ห้ามพัง)
   * @returns {Promise<{levels: Array, fromDatabase: boolean, warning: ?string}>}
   */
  list() {
    return get(ROOT);
  },

  /**
   * intent ที่ตั้ง Access Level ไว้ในทุก flow ที่ active (Advanced/Dynamic Router) + `countByCode`
   * @returns {Promise<{intents: Array, countByCode: Object.<string, number>, scannedFlows: number, scannedNodes: number, warnings: string[]}>}
   */
  usage() {
    return get(`${ROOT}/usage`);
  },

  /**
   * เพิ่มระดับ · `insertAfterCode` ว่าง ⇒ แทรกใต้ T3 (ตำแหน่งรองสุดท้าย) · 400 เมื่อ code ผิดรูป/ซ้ำ/แทรกหลัง T3
   * @param {{code: string, nameTh?: string, nameEn?: string, description?: string, insertAfterCode?: ?string}} body
   */
  create(body) {
    return post(`${ROOT}/create`, body);
  },

  /**
   * แก้ชื่อ/คำอธิบาย (Code แก้ไม่ได้หลังสร้าง)
   * @param {number} id
   * @param {{nameTh?: string, nameEn?: string, description?: string}} body
   */
  update(id, body) {
    return put(`${ROOT}/${encodeURIComponent(id)}`, body);
  },

  /**
   * ลบระดับ (hard delete) → ขั้นบันไดหลังลบ · T0/T1/T2/T3 ⇒ 400 · ยังมี intent ใช้ ⇒ throw
   * `httpStatus === 409` พร้อม `data = { code, intents }`
   * @param {number} id
   */
  remove(id) {
    return del(`${ROOT}/${encodeURIComponent(id)}`);
  },

  /**
   * บันทึกลำดับใหม่ — ต้องส่ง code ครบทุกตัว, T0 แรก, T3 ท้าย → ขั้นบันไดหลังบันทึก
   * @param {string[]} codes
   */
  reorder(codes) {
    return put(`${ROOT}/reorder`, { codes: Array.isArray(codes) ? codes : [] });
  },
};

export default accessLevelApi;
